/**
 * plp-variations.js - "Show all colours" toggle on PLP / search
 *
 * Each product card carries data-is-primary ("true" | "false"). Secondary colour products
 * (data-is-primary="false") are hidden by default via CSS and revealed when the toggle is on.
 *
 * The toggle adds/removes a `theme-show-all-colours` class on <body>, persisted in localStorage.
 * Hiding is handled in CSS (see product-list.liquid), so there is no flash of secondary cards on
 * load, and the body class survives filter/sort/paginate section re-renders (only the section
 * content is replaced, never <body>).
 *
 * When facet filters are active, product-list carries `product-list--filters-active` and the CSS
 * reveals all matching cards regardless of the toggle - filtering must never hide valid results.
 */

const STORAGE_KEY = 'plp-show-all-variants';
const BODY_CLASS = 'theme-show-all-colours';

// Resolve the toggle state. A saved customer preference wins; otherwise fall back to the
// server-rendered default (the `show_all_colours` theme setting applies BODY_CLASS in the layout),
// so enabling the setting doesn't get overridden the moment this script runs.
function isShowingAll() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored !== null) return stored === 'true';
  return document.body.classList.contains(BODY_CLASS);
}

function applyState(show) {
  document.body.classList.toggle(BODY_CLASS, show);
}

function syncToggles(show) {
  document.querySelectorAll('variation-toggle input[type="checkbox"]').forEach((input) => {
    input.checked = show;
  });
}

class VariationToggle extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    if (!this.checkbox) return;

    const show = isShowingAll();
    this.checkbox.checked = show;
    applyState(show);

    this.checkbox.addEventListener('change', () => {
      const isOn = this.checkbox.checked;
      localStorage.setItem(STORAGE_KEY, isOn);
      applyState(isOn);
      syncToggles(isOn);
    });
  }
}

// Re-sync toggle checkboxes after a section re-render (filter/sort/paginate). The body class
// persists across re-renders, so the CSS keeps applying without re-touching individual cards.
document.addEventListener('theme:loading:end', () => {
  syncToggles(isShowingAll());
});

if (!customElements.get('variation-toggle')) {
  customElements.define('variation-toggle', VariationToggle);
}
